import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import { createMember, type CreateMemberInput, type CreateMemberResponse } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { useCredentialRevealGuard } from "../auth/CredentialRevealGuard";
import { isValidCpf, normalizeBrazilianPhone } from "../api/identityValidation";
import { Button } from "../components/Button";
import { CredentialRevealPanel } from "../components/credentials/CredentialRevealPanel";
import { StatusBanner } from "../components/StatusBanner";
import { TextField } from "../components/TextField";
import { useDirtyFormProtection } from "../hooks/useDirtyFormProtection";

const emptyForm: CreateMemberInput = {
  full_name: "",
  cpf: "",
  birth_date: "",
  email: "",
  phone: "",
  username: "",
};

export function MemberCreatePage() {
  const { state, expireSession } = useAuth();
  const { canLeave, clearReveal, guardNavigation } = useCredentialRevealGuard();
  const [form, setForm] = useState<CreateMemberInput>(emptyForm);
  const [result, setResult] = useState<CreateMemberResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const guardDirtyNavigation = useDirtyFormProtection(!result && Object.values(form).some(Boolean));

  useEffect(() => () => clearReveal(), [clearReveal]);
  if (state.status !== "authenticated") return null;
  const accessToken = state.accessToken;

  function update(field: keyof CreateMemberInput, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (Object.values(form).some((value) => !value.trim())) {
      setError("Preencha todos os campos obrigatórios antes de continuar.");
      return;
    }
    if (!isValidCpf(form.cpf)) {
      setError("Informe um CPF válido.");
      return;
    }
    const phone = normalizeBrazilianPhone(form.phone);
    if (!phone) {
      setError("Informe um telefone brasileiro válido com DDD.");
      return;
    }
    setSaving(true);
    try {
      const response = await createMember(accessToken, {
        ...form,
        full_name: form.full_name.trim(),
        cpf: form.cpf.trim(),
        email: form.email.trim(),
        phone,
        username: form.username.trim(),
      });
      setResult(response);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        return;
      }
      setError(cause instanceof ApiError || cause instanceof NetworkError ? cause.message : "Não foi possível concluir o cadastro.");
    } finally {
      setSaving(false);
    }
  }

  if (result) {
    return (
      <main className="operational-page">
        <Link className="page-back" to="/associados" onClick={guardNavigation}>Voltar para associados</Link>
        <section className="form-card credential-result" aria-labelledby="member-created-title">
          <p className="members-page__eyebrow">Cadastro concluído</p>
          <h1 id="member-created-title">Associado cadastrado</h1>
          <p>O cadastro foi criado como Colaborador. A senha abaixo só será exibida nesta tela.</p>
          <CredentialRevealPanel username={result.username} password={result.temporary_password} />
          <StatusBanner tone="warning">Entregue a senha ao associado com segurança. Ela expira em 24 horas e deverá ser trocada no primeiro acesso.</StatusBanner>
          <div className="form-actions">
            <Button type="button" disabled={!canLeave} onClick={() => { setResult(null); setForm(emptyForm); clearReveal(); }}>Cadastrar outro</Button>
            <Link className="btn btn--secondary" to="/associados" onClick={guardNavigation}>Ir para associados</Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="operational-page">
      <Link className="page-back" to="/associados" onClick={guardDirtyNavigation}>Voltar para associados</Link>
      <section className="form-card" aria-labelledby="member-create-title">
        <p className="members-page__eyebrow">Secretaria e DAF</p>
        <h1 id="member-create-title">Novo associado</h1>
        <p className="form-card__intro">Cadastro inicial com emissão de senha temporária. A categoria será Colaborador.</p>
        {error && <StatusBanner tone="error">{error}</StatusBanner>}
        <form onSubmit={submit} noValidate>
          <div className="form-grid">
            <TextField label="Nome completo" required value={form.full_name} onChange={(event) => update("full_name", event.target.value)} />
            <TextField label="CPF" required inputMode="numeric" value={form.cpf} onChange={(event) => update("cpf", event.target.value)} />
            <TextField label="Data de nascimento" required type="date" value={form.birth_date} onChange={(event) => update("birth_date", event.target.value)} />
            <TextField label="E-mail" required type="email" value={form.email} onChange={(event) => update("email", event.target.value)} />
            <TextField label="Telefone" required type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} />
            <TextField label="Nome de usuário" required value={form.username} hint="Será usado no acesso ao portal." onChange={(event) => update("username", event.target.value)} />
          </div>
          <div className="form-actions">
            <Link className="btn btn--secondary" to="/associados" onClick={guardDirtyNavigation}>Cancelar</Link>
            <Button type="submit" loading={saving}>Criar cadastro</Button>
          </div>
        </form>
      </section>
    </main>
  );
}
